/**
 * PlaceAutocomplete - Google Places 自動完成地點搜尋輸入框
 * 500ms debounce，< 2 字元不發送請求
 * 使用新版 Places API（AutocompleteSuggestion + Place）
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { getPlacePredictions, getPlaceDetails } from '../../services/placesService';
import type { PlaceSuggestion } from '../../services/placesService';

export interface PlaceSelectResult {
  name: string;
  lat: number;
  lng: number;
  placeId: string;
  address: string;
  /** 僅在帶入既有行程的地點資料時才會有值；透過此元件重新搜尋選取的新地點不會有照片 */
  photoReference?: string | null;
}

interface PlaceAutocompleteProps {
  value: string;
  onInputChange: (value: string) => void;
  onPlaceSelect: (place: PlaceSelectResult) => void;
  placeholder?: string;
  error?: string;
}

export default function PlaceAutocomplete({
  value,
  onInputChange,
  onPlaceSelect,
  placeholder = '搜尋地點',
  error,
}: PlaceAutocompleteProps) {
  // useMapsLibrary('places') 確保 Places library 已載入
  const placesLib = useMapsLibrary('places');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const divRef = useRef<HTMLDivElement>(null);

  // 建立 session token（Places library 載入後）
  useEffect(() => {
    if (!placesLib) return;
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
  }, [placesLib]);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!placesLib || input.length < 2) {
        setSuggestions([]);
        setIsOpen(false);
        return;
      }

      setIsLoading(true);
      setServiceError(null);

      try {
        const results = await getPlacePredictions(
          input,
          sessionTokenRef.current ?? undefined
        );
        setSuggestions(results);
        setIsOpen(results.length > 0);
      } catch {
        setServiceError('地點搜尋服務暫時無法使用，您仍可手動填寫地點名稱');
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [placesLib]
  );

  // Debounce 輸入
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (value.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, fetchSuggestions]);

  useClickOutside(divRef, () => setIsOpen(false));

  const handleSelect = async (suggestion: PlaceSuggestion) => {
    setIsOpen(false);
    setSuggestions([]);
    onInputChange(suggestion.mainText);

    try {
      const details = await getPlaceDetails(suggestion.placePrediction);

      if (details) {
        onPlaceSelect({
          name: details.name,
          lat: details.lat,
          lng: details.lng,
          placeId: suggestion.placeId,
          address: details.address,
        });
      }

      // 選取後重置 session token
      if (placesLib) {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      }
    } catch {
      // 取得詳情失敗時仍保留使用者選取的文字
    }
  };

  return (
    <div ref={divRef} className="relative">
      <div className="relative flex items-center">
        <svg
          className="w-5 h-5 absolute left-4 text-slate-400 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <input
          className="w-full rounded-lg border border-slate-200 bg-white pl-11 pr-10 py-3.5 text-base text-slate-900 placeholder-slate-400 focus:border-[#0dccf2] focus:ring-1 focus:ring-[#0dccf2] outline-none"
          placeholder={placeholder}
          type="text"
          value={value}
          onChange={(e) => onInputChange(e.target.value)}
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-[#0dccf2] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* 服務錯誤提示 */}
      {serviceError && (
        <p className="text-amber-600 text-sm mt-1">{serviceError}</p>
      )}

      {/* 驗證錯誤 */}
      {error && !serviceError && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}

      {/* 建議下拉清單 */}
      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden">
          {suggestions.map((suggestion) => (
            <li key={suggestion.placeId}>
              <button
                type="button"
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex flex-col gap-0.5"
                onMouseDown={(e) => {
                  // mousedown 而非 click，避免 blur 先觸發導致清單消失
                  e.preventDefault();
                  handleSelect(suggestion);
                }}
              >
                <span className="text-slate-900 text-sm font-semibold leading-tight">
                  {suggestion.mainText}
                </span>
                <span className="text-slate-400 text-xs leading-tight">
                  {suggestion.secondaryText}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
